/******* Angular Resourse *******/
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';

/******* Core Resourse *******/
import { CoreConfigService } from '@core/services/config.service';
import { AccountSwitchService } from '@core/services/account-switch.service';
import { UtilsService } from '@core/services/utils.service';
import { AccessTokenService } from '@core/services/access-token.service';
import { GlobalUIBlockingService } from '@core/services/global-ui-blocking.service';
import { ReCaptchaV3Service } from '@core/services/recaptcha.service';
import { CoreConfig, SsoProvider, SystemPlatform } from '@core/types';

/******* API Resourse *******/
import { Auth } from '@api';

/******* Plug-In Resourse *******/
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CoreTranslatingService } from '@core/services/core-translating.service';

/******* Environments Resourse *******/
import { environment } from 'environments/environment';

/******* Component Resourse *******/
import { SsoOptionComponent } from './modal-template/sso-option.component';
import { GOOGLE_CLIENT_ID, GOOGLE_SECRET } from '@core/constant';
import { TranslateService } from '@ngx-translate/core';

const USER_SERVICE_URL: string = environment.userServiceUrl;

@Component({
        selector: 'app-login',
        templateUrl: './login.component.html',
        styleUrls: ['./login.component.scss'],
        encapsulation: ViewEncapsulation.Emulated
})
export class LoginComponent implements OnInit, OnDestroy {

        // Public
        public coreConfig: CoreConfig;
        public loginForm: UntypedFormGroup;
        public submitted: boolean = false;
        public loading: boolean = false;

        // Private
        private _unsubscribeAll$: Subject<any> = new Subject();

        /**
         * Constructor
         */
        public currentLang: string = 'en';

        constructor(
                private _router: Router,
                private _title: Title,
                private _formBuilder: UntypedFormBuilder,
                private _elementRef: ElementRef,
                private _auth: Auth,
                private _coreConfigService: CoreConfigService,
                private _utilsService: UtilsService,
                private _accessTokenService: AccessTokenService,
                private _accountSwitchService: AccountSwitchService,
                private _globalUIBlockingService: GlobalUIBlockingService,
                private _reCaptchaV3Service: ReCaptchaV3Service,
                @Inject(DOCUMENT) private readonly _document: Document,
                private _ngbModal: NgbModal,
                private _coreTranslatingService: CoreTranslatingService
        ,
                private _translate: TranslateService) {
                this.initializeComponent();
        }

        /**
         * Accessors
         */
        get currentVersion(): string {
                const version = environment.versionControl;
                return this._utilsService.getPlatform === SystemPlatform.STANDARD
                        ? version
                        : `${version}.${this._utilsService.getPlatform}`;
        }

        get isInternetFunctionEnable(): boolean {
                return this._utilsService.platformConfig.isReCaptchaEnable;
        }

        /**
         * Lifecycle Hooks
         */
        ngOnInit(): void {
                this._coreConfigService.config
                        .pipe(takeUntil(this._unsubscribeAll$))
                        .subscribe(config => this.coreConfig = config);

                this._globalUIBlockingService.enforceToStop();
                this.handlePersistentLogin();
        }

        ngOnDestroy(): void {
                this._unsubscribeAll$.next(null);
                this._unsubscribeAll$.complete();
        }

        /**
         * Public Methods
         */

        /**
         * SSO Login
         */
        public ssoLogin(): void {
                this._ngbModal.open(SsoOptionComponent, {
                        centered: true,
                        size: 'sm'
                }).result.then(
                        (result) => this.handleSsoProvider(result),
                        () => { } // Dismiss handler
                );
        }

        /**
         * Submit Login Form
         */
        public onSubmit(): void {
                this.submitted = true;

                if (this.loginForm.invalid) {
                        this._utilsService.scrollToInvalid(this._elementRef);
                        return;
                }

                this.loading = true;
                this._reCaptchaV3Service.execute('LOGIN')
                        .then((token) => this.performLogin(token))
                        .catch((error) => this.handleLoginError(error));
        }

        /**
         * Private Methods
         */

        /**
         * Initialize Component
         */
        private initializeComponent(): void {
                this.clearStorage();
                this._title.setTitle("BECentral - BEC Technologies Inc.");
                this.buildLoginForm();
                this.configureLayout();
        }

        /**
         * Clear Storage
         */
        private clearStorage(): void {
                sessionStorage.clear();
                this._coreTranslatingService.preserveLangAcrossClear();
                this.currentLang = this._coreTranslatingService.currentLang;
        }

        public switchLanguage(lang: string): void {
                this.currentLang = lang;
                this._coreTranslatingService.switchLanguage(lang);
        }

        /**
         * Build Login Form
         */
        private buildLoginForm(): void {
                this.loginForm = this._formBuilder.group({
                        username: ['', [Validators.required]],
                        password: ['', [Validators.required]]
                });
        }

        /**
         * Configure Layout
         */
        private configureLayout(): void {
                this._coreConfigService.setConfig({
                        layout: {
                                footer: { hidden: true },
                                menu: { hidden: true },
                                navbar: { hidden: true },
                                buyNow: false,
                                customizer: false,
                                scrollTop: false,
                                enableLocalStorage: false,
                                skin: "dark",
                                type: "vertical"
                        }
                });
        }

        /**
         * Handle Persistent Login via Cookie
         */
        private handlePersistentLogin(): void {
                const cookieMap = this.parseCookies();
                if (cookieMap.hasOwnProperty('username')) {
                        this._accountSwitchService.switchAccount({ accessToken: '', refreshToken: '' });
                }
        }

        /**
         * Parse Cookies
         */
        private parseCookies(): Record<string, string> {
                if (!this._document.cookie) return {};

                return this._document.cookie.split(';').reduce((acc, cur) => {
                        const [key, value] = cur.split('=');
                        return { ...acc, [key.trim()]: decodeURIComponent(value) };
                }, {} as Record<string, string>);
        }

        /**
         * Handle SSO Provider Selection
         */
        private handleSsoProvider(provider: SsoProvider): void {
                switch (provider) {
                        case SsoProvider.GOOGLE:
                                this.initiateGoogleAuth();
                                break;
                        case SsoProvider.PINGONE:
                                this._globalUIBlockingService.start();
                                // Add PingOne implementation
                                break;
                }
        }

        /**
         * Initiate Google Authentication
         */
        private initiateGoogleAuth(): void {
                this._globalUIBlockingService.start();
                const hostName = this.getAuthHostname();
                const url = `https://${hostName}${USER_SERVICE_URL}auth/google?client_id=${GOOGLE_CLIENT_ID}&client_secret=${GOOGLE_SECRET}`;
                window.location.href = url;
        }

        /**
         * Get Authentication Hostname
         */
        private getAuthHostname(): string {
                const hostName = window.location.hostname;
                return hostName === '127.0.0.1' ? 'staging-app.becentral.io' : hostName;
        }

        /**
         * Perform Login
         */
        private performLogin(token: string): void {
                const formValue = this.loginForm.value;
                const inputData = {
                        clientSecret: environment.CLIENT_SECRET,
                        clientId: environment.CLIENT_ID,
                        username: formValue.username.toLowerCase(),
                        password: formValue.password,
                        captchaToken: token
                };

                this._auth.login(inputData).subscribe({
                        next: (results) => this.handleLoginResponse(results.data, formValue),
                        error: (error) => this.handleLoginError(error)
                });
        }

        /**
         * Handle Login Response
         */
        private handleLoginResponse(returnData: any, formValue: any): void {
                if (returnData.code) {
                        this.handleAuthorizationCode(returnData, formValue);
                } else if (returnData.enforceTwoFA) {
                        this.handleEnforceTwoFactor(returnData, formValue);
                } else if (returnData.isTwoFactorActivity !== undefined) {
                        this.handleTwoFactorRequired(returnData, formValue);
                } else if (returnData.verified === false) {
                        this._utilsService.alert('warning', this._translate.instant('AUTH.EMAIL_UNVERIFIED'), this._translate.instant('AUTH.VERIFY_FROM_EMAIL'));
                }
                this.submitted = false;
        }

        /**
         * Handle Authorization Code Flow
         */
        private handleAuthorizationCode(returnData: any, formValue: any): void {
                this._accessTokenService.initialAccessToken({
                        code: returnData.code,
                        clientId: environment.CLIENT_ID,
                        username: formValue.username.toLowerCase()
                }).subscribe({
                        next: (response) => this._accountSwitchService.switchAccount(response.data),
                        error: (error) => this.handleLoginError(error, "Fail to Sign In.")
                });
        }

        /**
         * Handle Enforce Two Factor Authentication
         */
        private handleEnforceTwoFactor(returnData: any, formValue: any): void {
                if (returnData.isTwoFactorActivity) {
                        const hash = this.encodeFormData(formValue, returnData);
                        this._router.navigate(['/twofa'], { queryParams: { token: hash } });
                } else {
                        const queryParams = {
                                email: formValue.username.toLowerCase(),
                                token: returnData.token,
                                activation: true,
                                termAgreement: returnData.termAgreement || false
                        };
                        this._router.navigate(['/verify-twofa'], { queryParams });
                }
        }

        /**
         * Handle Two Factor Authentication Required
         */
        private handleTwoFactorRequired(returnData: any, formValue: any): void {
                if (!returnData.isTwoFactorActivity) {
                        this.sendAuthCodeEmail(formValue.username.toLowerCase());
                }

                const hash = this.encodeFormData(formValue, returnData);
                this._router.navigate(['/twofa'], { queryParams: { token: hash } });
        }

        /**
         * Send Authentication Code Email
         */
        private sendAuthCodeEmail(username: string): void {
                this._auth.sendingAuthCodeEmail({ username }).subscribe({
                        next: () => this._utilsService.alert('warning', this._translate.instant('AUTH.RECAPTCHA_LOW_SCORE'), this._translate.instant('AUTH.CHECK_EMAIL_2FA')),
                        error: (error) => this._utilsService.alert('error', this._translate.instant('AUTH.RECAPTCHA_LOW_SCORE'), error)
                });
        }

        /**
         * Encode Form Data with Return Data
         */
        private encodeFormData(formValue: any, returnData: any): string {
                const data = {
                        username: formValue.username.toLowerCase(),
                        password: formValue.password,
                        ...returnData
                };
                return window.btoa(JSON.stringify(data));
        }

        /**
         * Handle Login Error
         */
        private handleLoginError(error: any, title: string = null): void {
                if (!title) title = this._translate.instant('AUTH.FAIL_TO_SIGN_IN');
                this._utilsService.alert('error', title, error);
                this.loading = false;
                this.submitted = false;
        }
}
